import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Clock, Heart, Home, Baby, Briefcase, AlertCircle, ArrowRight, CheckCircle2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const eventIcons = [Briefcase, Home, Heart, Baby];

export const SpecialEnrollmentPage = () => {
  const { t } = useTranslation();
  const qualifyingEvents = t('specialEnrollment.events', { returnObjects: true });
  const additionalEvents = t('specialEnrollment.additionalEvents', { returnObjects: true });
  const notSureItems = t('specialEnrollment.notSureItems', { returnObjects: true });

  return (
    <div className="overflow-hidden" data-testid="special-enrollment-page">
      <section className="gradient-hero py-20 lg:py-32" data-testid="sep-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-600 rounded-full text-sm font-medium mb-6">
              <Clock className="h-4 w-4" />
              {t('specialEnrollment.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              {t('specialEnrollment.heroTitle')} <span className="text-gradient">{t('specialEnrollment.heroHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">{t('specialEnrollment.heroDesc')}</p>
            <Link to="/contact">
              <Button className="btn-accent" data-testid="sep-hero-cta">
                {t('specialEnrollment.heroCta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-6 bg-amber-50 border-y border-amber-100" data-testid="sep-urgency">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0" />
            <p className="text-amber-800">
              <span className="font-semibold">{t('specialEnrollment.urgencyLabel')}</span> {t('specialEnrollment.urgencyText1')}
              <span className="font-bold"> {t('specialEnrollment.urgencyDays')}</span> {t('specialEnrollment.urgencyText2')}
            </p>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="sep-events">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('specialEnrollment.eventsTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('specialEnrollment.eventsSubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {qualifyingEvents.map((event, index) => {
              const Icon = eventIcons[index] || Briefcase;
              return (
                <motion.div key={event.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-8 rounded-2xl border border-slate-100 card-shadow" data-testid={`event-card-${index}`}>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="h-14 w-14 bg-sky-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Icon className="h-7 w-7 text-sky-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-1">{event.title}</h3>
                      <p className="text-slate-600 text-sm">{event.description}</p>
                    </div>
                  </div>
                  <div className="space-y-2 mb-4">
                    {event.examples.map((example) => (
                      <div key={example} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-slate-700 text-sm">{example}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                    <Calendar className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-amber-600 font-medium">{t('specialEnrollment.windowLabel', { timeframe: event.timeframe })}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="sep-additional">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('specialEnrollment.additionalTitle')}</h2>
              <p className="text-lg text-slate-600 mb-8">{t('specialEnrollment.additionalText')}</p>
              <ul className="space-y-4">
                {additionalEvents.map((event) => (
                  <li key={event} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{event}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white rounded-2xl p-8 border border-slate-100 card-shadow">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{t('specialEnrollment.notSureTitle')}</h3>
              <p className="text-slate-600 mb-6">{t('specialEnrollment.notSureText')}</p>
              <div className="space-y-4 mb-8">
                {notSureItems.map((item) => (
                  <div key={item} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="btn-accent w-full" data-testid="sep-check-btn">{t('specialEnrollment.checkBtn')}</Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900" data-testid="sep-cta-section">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Clock className="h-12 w-12 text-sky-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('specialEnrollment.ctaTitle')}</h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">{t('specialEnrollment.ctaText')}</p>
            <Link to="/contact">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="sep-final-cta">
                {t('specialEnrollment.ctaBtn')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SpecialEnrollmentPage;
